import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import styles from '../../styles/gri/gri200.module.css'
import reportStyles from '../../styles/gri/report.module.css'
import { get } from '../../utils/api'
import StyledReportTable from './StyledReportTable'

const GRI200s = () => {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState(null)

  const { id } = useParams()
  useEffect(() => {
    fetchData()
  }, [id])

  const fetchData = async () => {
    try {
      setLoading(true)
       const res = await get(`gri/get?type=GRI200&Id=${id}`)
       console.log(res.data.data, 'res')
      const fetchedData = res.data.data.GRI200
      setData(fetchedData)
    } catch (err) {
      console.log('Error fetching GRI data:', err)
    } finally {
      setLoading(false)
    }
  }

  // Prepare columns for Direct economic value generated table
  const directValueGeneratedColumns = [
    { field: 'economicValueGenerated', label: 'Economic Value Generated', width: '60%' },
    { field: 'amount', label: 'Amount', width: '40%' }
  ]

  // Prepare rows for Direct economic value generated
  const directValueGeneratedRows = data?.valueGenerated?.amount
    ? (() => {
      const amount = data.valueGenerated.amount
      const rows = []

      // Add individual rows (excluding total)
      if (amount.sales !== undefined) {
        rows.push({
          economicValueGenerated: 'Net sales',
          amount: typeof amount.sales === 'number' ? amount.sales.toLocaleString() : amount.sales
        })
      }
      if (amount.revenueFinancial !== undefined) {
        rows.push({
          economicValueGenerated: 'Revenues from financial investments',
          amount: typeof amount.revenueFinancial === 'number' ? amount.revenueFinancial.toLocaleString() : amount.revenueFinancial
        })
      }
      if (amount.revenueSale !== undefined) {
        rows.push({
          economicValueGenerated: 'Revenues from sale of assets',
          amount: typeof amount.revenueSale === 'number' ? amount.revenueSale.toLocaleString() : amount.revenueSale
        })
      }

      // Add total row
      if (amount.total !== undefined) {
        rows.push({
          economicValueGenerated: '',
          amount: typeof amount.total === 'number' ? amount.total.toLocaleString() : amount.total,
          isTotal: true
        })
      }

      return rows
    })()
    : []

  // Prepare columns for Economic value distributed table
  const economicValueDistributedColumns = [
    { field: 'economicValueDistributed', label: 'Economic Value Distributed', width: '60%' },
    { field: 'amount', label: 'Amount', width: '40%' }
  ]

  // Prepare rows for Economic value distributed
  const economicValueDistributedRows = data?.valueDistributed?.amount
    ? (() => {
      const amount = data.valueDistributed.amount
      const rows = []

      // Add individual rows (excluding total)
      if (amount.cost !== undefined) {
        rows.push({
          economicValueDistributed: 'Operating costs',
          amount: typeof amount.cost === 'number' ? amount.cost.toLocaleString() : amount.cost
        })
      }
      if (amount.empbenefits !== undefined) {
        rows.push({
          economicValueDistributed: 'Employee wages and benefits',
          amount: typeof amount.empbenefits === 'number' ? amount.empbenefits.toLocaleString() : amount.empbenefits
        })
      }
      if (amount.capitalPayment !== undefined) {
        rows.push({
          economicValueDistributed: 'Payments to providers of capital',
          amount: typeof amount.capitalPayment === 'number' ? amount.capitalPayment.toLocaleString() : amount.capitalPayment
        })
      }
      if (amount.governmentPayment !== undefined) {
        rows.push({
          economicValueDistributed: 'Payments to government',
          amount: typeof amount.governmentPayment === 'number' ? amount.governmentPayment.toLocaleString() : amount.governmentPayment
        })
      }
      if (amount.community !== undefined) {
        rows.push({
          economicValueDistributed: 'Community investments',
          amount: typeof amount.community === 'number' ? amount.community.toLocaleString() : amount.community
        })
      }

      // Add total row
      if (amount.total !== undefined) {
        rows.push({
          economicValueDistributed: '',
          amount: typeof amount.total === 'number' ? amount.total.toLocaleString() : amount.total,
          isTotal: true
        })
      }

      return rows
    })()
    : []

  // Prepare columns for Economic value retained table
  const economicValueRetainedColumns = [
    { field: 'economicValueRetained', label: 'Economic Value Retained', width: '60%' },
    { field: 'amount', label: 'Amount', width: '40%' }
  ]

  // Prepare rows for Economic value retained
  // Calculate from first two tables: Direct economic value generated (from table 1) and Economic value distributed (from table 2)
  const economicValueRetainedRows = (() => {
    const rows = []
    const valueGeneratedTotal = data?.valueGenerated?.amount?.total
    const valueDistributedTotal = data?.valueDistributed?.amount?.total

    // First row: Direct economic value generated (from first table total)
    if (valueGeneratedTotal !== undefined && valueGeneratedTotal !== null) {
      rows.push({
        economicValueRetained: 'Direct economic value generated',
        amount: typeof valueGeneratedTotal === 'number' ? valueGeneratedTotal.toLocaleString() : valueGeneratedTotal
      })
    }

    // Second row: Economic value distributed (from second table total)
    if (valueDistributedTotal !== undefined && valueDistributedTotal !== null) {
      rows.push({
        economicValueRetained: 'Economic value distributed',
        amount: typeof valueDistributedTotal === 'number' ? valueDistributedTotal.toLocaleString() : valueDistributedTotal
      })
    }

    // Total row: Calculate difference (valueGenerated - valueDistributed)
    if (valueGeneratedTotal !== undefined && valueDistributedTotal !== undefined) {
      const totalRetained = typeof valueGeneratedTotal === 'number' && typeof valueDistributedTotal === 'number'
        ? valueGeneratedTotal - valueDistributedTotal
        : (data?.valueRetained?.amount?.total || 0)

      rows.push({
        economicValueRetained: '',
        amount: typeof totalRetained === 'number' ? totalRetained.toLocaleString() : totalRetained,
        isTotal: true
      })
    }

    return rows
  })()

  // Get currency for titles
  const currency = data?.valueGeneratedCurrency || data?.valueDistributedCurrency || data?.valueRetainedCurrency || 'N/A'

  const questionData = [
    {
      question: "a. Direct economic value generated and distributed (EVG&D) on an accruals basis, including the basic components for the organization's global operations as listed below. If data are presented on a cash basis, report the justification for this decision in addition to reporting the following basic components:",
      subQuestion: [
        {
          question: `i. Direct economic value generated (Currency- ${currency}):`,
          columns: directValueGeneratedColumns,
          rows: directValueGeneratedRows,
          showSerialNumber: true
        },
        {
          question: `ii. Economic value distributed (Currency- ${currency}):`,
          columns: economicValueDistributedColumns,
          rows: economicValueDistributedRows,
          showSerialNumber: true
        },
        {
          question: `iii. Economic value retained (Currency- ${currency}):`,
          columns: economicValueRetainedColumns,
          rows: economicValueRetainedRows,
          showSerialNumber: true
        }
      ]
    },
  ]

  if (loading) {
    return (
      <div className={styles.container}>
        <p>Loading...</p>
      </div>
    )
  }

  return (
    <div className={styles.container}>
    {/* Main Heading */}
      <h1 className={reportStyles.sectionHeading}>
      201 : Economic Performance
      </h1>
      <h1 className={reportStyles.mainHeading}>
        201-1 : Direct economic value generated and distributed
      </h1>

      {/* Render questions dynamically */}
      {questionData.map((questionItem, index) => (
        <div key={index}>
          {/* Render main question if it has table data */}
          {questionItem.columns && questionItem.rows && (
            <StyledReportTable
              title={questionItem.question}
              columns={questionItem.columns}
              rows={questionItem.rows}
              showSerialNumber={questionItem.showSerialNumber !== false}
            />
          )}

          {/* Render sub-questions */}
          {questionItem.subQuestion && questionItem.subQuestion.map((subQ, subIndex) => (
            <StyledReportTable
              key={subIndex}
              title={subQ.question}
              columns={subQ.columns || []}
              rows={subQ.rows || []}
              showSerialNumber={subQ.showSerialNumber !== false}
            />
          ))}
        </div>
      ))}

      {/* Section b: Significant EVG&D impacts */}
      <div className={reportStyles.consolidatingSection}>
        <p className={reportStyles.consolidatingTitle}>
          b. Report on significant EVG&D impacts separately at country, regional, or market levels? If yes, specify the reporting level(s) and the criteria used to define significance.
        </p>
        <div className={reportStyles.questionsContainer}>
          <div className={reportStyles.questionBox}>
            <p className={reportStyles.questionAnswer}>
              {data?.significantEVGandDDetails || 'No information provided.'}
            </p>
          </div>
        </div>
      </div>

      {/* Section 201-2: Financial implications and other risks and opportunities due to climate change */}
      <h1 className={reportStyles.mainHeading}>
        201-2: Financial implications and other risks and opportunities due to climate change
      </h1>

      <div className={reportStyles.consolidatingSection}>
        <p className={reportStyles.consolidatingTitle}>
          a. Risks and opportunities posed by climate change that have the potential to generate substantive changes in operations, revenue, or expenditure, including:
        </p>
        <div className={reportStyles.questionsContainer}>
          <div className={reportStyles.questionBox}>
            <p className={reportStyles.questionTitle}>
              i. Description of the risk or opportunity and its classification as either physical, regulatory, or other;
            </p>
            <p className={reportStyles.questionAnswer}>
              {data?.riskOrOpportunityDescription || 'No information provided.'}
            </p>
          </div>

          <div className={reportStyles.questionBox}>
            <p className={reportStyles.questionTitle}>
              ii. Description of the impact associated with the risk or opportunity;
            </p>
            <p className={reportStyles.questionAnswer}>
              {data?.descriptionOfImpact || 'No information provided.'}
            </p>
          </div>

          <div className={reportStyles.questionBox}>
            <p className={reportStyles.questionTitle}>
              iii. The financial implications of the risk or opportunity before action is taken;
            </p>
            <p className={reportStyles.questionAnswer}>
              {data?.financialImplications || 'No information provided.'}
            </p>
          </div>

          <div className={reportStyles.questionBox}>
            <p className={reportStyles.questionTitle}>
              iv. The methods used to manage the risk or opportunity;
            </p>
            <p className={reportStyles.questionAnswer}>
              {data?.methodsUsedToManage || 'No information provided.'}
            </p>
          </div>

          <div className={reportStyles.questionBox}>
            <p className={reportStyles.questionTitle}>
              v. The costs of actions taken to manage the risk or opportunity.
            </p>
            <p className={reportStyles.questionAnswer}>
              {data?.costsOfActions || 'No information provided.'}
            </p>
          </div>
        </div>
      </div>

      {/* Section 201-3: Defined benefit plan obligations and other retirement plans */}
      <h1 className={reportStyles.mainHeading}>
        201-3: Defined benefit plan obligations and other retirement plans
      </h1>

      <div className={reportStyles.consolidatingSection}>
        {/* Subsection a */}
        <p className={reportStyles.consolidatingTitle}>
          a. If the plan's liabilities are met by the organization's general resources, the estimated value of those liabilities.
        </p>
        <div className={reportStyles.questionsContainer}>
          <div className={reportStyles.questionBox}>
            <p className={reportStyles.questionAnswer}>
              Amount: {data?.plansLiablities?.amount
                ? `${data.plansLiablities.currency || ''} ${typeof data.plansLiablities.amount === 'number'
                  ? data.plansLiablities.amount.toLocaleString()
                  : data.plansLiablities.amount}`.trim()
                : 'No information provided.'}
            </p>
          </div>
        </div>

        {/* Subsection b */}
        <p className={reportStyles.consolidatingTitle}>
          b. If a separate fund exists to pay the plan's pension liabilities:
        </p>
        <div className={reportStyles.questionsContainer}>
          <div className={reportStyles.questionBox}>
            <p className={reportStyles.questionTitle}>
              i. The extent to which the scheme's liabilities are estimated to be covered by the assets that have been set aside to meet them
            </p>
            <p className={reportStyles.questionAnswer}>
              Amount: {data?.schemesLibilites?.amount
                ? `${data.schemesLibilites.currency || ''} ${typeof data.schemesLibilites.amount === 'number'
                  ? data.schemesLibilites.amount.toLocaleString()
                  : data.schemesLibilites.amount}`.trim()
                : 'No information provided.'}
            </p>
          </div>

          <div className={reportStyles.questionBox}>
            <p className={reportStyles.questionTitle}>
              ii. The basis on which that estimate has been arrived at:
            </p>
            <p className={reportStyles.questionAnswer}>
              Amount: {data?.estimateArrived || 'No information provided.'}
            </p>
          </div>

          <div className={reportStyles.questionBox}>
            <p className={reportStyles.questionTitle}>
              iii. When that estimate was made
            </p>
            <p className={reportStyles.questionAnswer}>
              Date: {data?.estimateMade
                ? (() => {
                  try {
                    const date = new Date(data.estimateMade)
                    const day = String(date.getDate()).padStart(2, '0')
                    const month = String(date.getMonth() + 1).padStart(2, '0')
                    const year = date.getFullYear()
                    return `${month}/${day}/${year}`
                  } catch (e) {
                    return data.estimateMade
                  }
                })()
                : 'No information provided.'}
            </p>
          </div>
        </div>

        {/* Subsection c */}
        <p className={reportStyles.consolidatingTitle}>
          c. If a fund set up to pay the plan's pension liabilities is not fully covered, explain the strategy, if any, adopted by the employer to work towards full coverage, and the timescale, if any, by which the employer hopes to achieve full coverage.
        </p>
        <div className={reportStyles.questionsContainer}>
          <div className={reportStyles.questionBox}>
            <p className={reportStyles.questionAnswer}>
              {data?.employerFullCoverage || 'No information provided.'}
            </p>
          </div>
        </div>

        {/* Subsection d */}
        <p className={reportStyles.consolidatingTitle}>
          d. Percentage of salary contributed by employee or employer.
        </p>
        <div className={reportStyles.questionsContainer}>
          <div className={reportStyles.questionBox}>
            <p className={reportStyles.questionAnswer}>
              {data?.salaryContributes
                ? `${data.salaryContributes}${data.salaryContributes.includes('%') ? '' : '%'}`
                : 'No information provided.'}
            </p>
          </div>
        </div>

        {/* Subsection e */}
        <p className={reportStyles.consolidatingTitle}>
          e. Level of participation in retirement plans, such as participation in mandatory or voluntary schemes, regional, or country-based schemes, or those with financial impact.
        </p>
        <div className={reportStyles.questionsContainer}>
          <div className={reportStyles.questionBox}>
            <p className={reportStyles.questionAnswer}>
              {data?.retirementPlanParticipation || 'No information provided.'}
            </p>
          </div>
        </div>
      </div>

      {/* Section 201-4: Financial assistance received from government */}
      <h1 className={reportStyles.mainHeading}>
        201-4: Financial assistance received from government
      </h1>

      {/* Subsection a: Total monetary value table */}
      <div className={reportStyles.consolidatingSection}>
        <p className={reportStyles.consolidatingTitle}>
          a. Total monetary value of financial assistance received by the organization from any government during the reporting period, including:
        </p>
        
        {/* First Table: Total Financial Assistance */}
        {(() => {
          const assistanceColumns = [
            { field: 'type', label: 'Type of Financial Assistance', width: '60%' },
            { field: 'amount', label: 'Amount', width: '40%' }
          ]

          const assistanceRows = data?.finalcalAssistance
            ? (() => {
                const assistance = data.finalcalAssistance
                const rows = []
                
                if (assistance.reliefCredits !== undefined && assistance.reliefCredits !== null && assistance.reliefCredits !== '') {
                  rows.push({
                    type: 'Tax relief and tax credits',
                    amount: typeof assistance.reliefCredits === 'number' ? assistance.reliefCredits.toLocaleString() : assistance.reliefCredits
                  })
                }
                if (assistance.subsidies !== undefined && assistance.subsidies !== null && assistance.subsidies !== '') {
                  rows.push({
                    type: 'Subsidies',
                    amount: typeof assistance.subsidies === 'number' ? assistance.subsidies.toLocaleString() : assistance.subsidies
                  })
                }
                if (assistance.holidays !== undefined && assistance.holidays !== null && assistance.holidays !== '') {
                  rows.push({
                    type: 'Royalty Holidays',
                    amount: typeof assistance.holidays === 'number' ? assistance.holidays.toLocaleString() : assistance.holidays
                  })
                }
                if (assistance.incentives !== undefined && assistance.incentives !== null && assistance.incentives !== '') {
                  rows.push({
                    type: 'Financial incentives',
                    amount: typeof assistance.incentives === 'number' ? assistance.incentives.toLocaleString() : assistance.incentives
                  })
                }
                if (assistance.ecas !== undefined && assistance.ecas !== null && assistance.ecas !== '') {
                  rows.push({
                    type: 'Financial assistance from Export Credit Agencies (ECAs)',
                    amount: typeof assistance.ecas === 'number' ? assistance.ecas.toLocaleString() : assistance.ecas
                  })
                }
                if (assistance.investmentgrant !== undefined && assistance.investmentgrant !== null && assistance.investmentgrant !== '') {
                  rows.push({
                    type: 'Investment grants, research and development grants, and other relevant types of grant',
                    amount: typeof assistance.investmentgrant === 'number' ? assistance.investmentgrant.toLocaleString() : assistance.investmentgrant
                  })
                }
                if (assistance.awards !== undefined && assistance.awards !== null && assistance.awards !== '') {
                  rows.push({
                    type: 'Awards',
                    amount: typeof assistance.awards === 'number' ? assistance.awards.toLocaleString() : assistance.awards
                  })
                }
                if (assistance.other !== undefined && assistance.other !== null && assistance.other !== '') {
                  rows.push({
                    type: 'Other financial benefits received or receivable from any government for any operation',
                    amount: typeof assistance.other === 'number' ? assistance.other.toLocaleString() : assistance.other
                  })
                }
                
                // Add total row
                if (assistance.total !== undefined && assistance.total !== null && assistance.total !== '') {
                  rows.push({
                    type: '',
                    amount: typeof assistance.total === 'number' ? assistance.total.toLocaleString() : assistance.total,
                    isTotal: true
                  })
                }
                
                return rows
              })()
            : []

          return (
            <>
              {assistanceRows.length > 0 && (
                <p className={reportStyles.questionAnswer} style={{ marginLeft: '16px', marginBottom: '16px' }}>
                  Currency: {data?.finalcalAssistanceCurrency || 'N/A'}
                </p>
              )}
              <StyledReportTable
                columns={assistanceColumns}
                rows={assistanceRows}
                showSerialNumber={true}
              />
            </>
          )
        })()}
      </div>

      {/* Subsection b: Country-wise financial assistance */}
      <div className={reportStyles.consolidatingSection}>
        <p className={reportStyles.consolidatingTitle}>
          b. Country wise, Total monetary value of financial assistance received by the organization from any government during the reporting period, including:
        </p>

        {/* Second Table: Country-wise Financial Assistance */}
        {data?.CountrywisefinalcalAssistance && data.CountrywisefinalcalAssistance.length > 0
          ? data.CountrywisefinalcalAssistance.map((countryItem, countryIndex) => {
            const countryColumns = [
              { field: 'type', label: 'Type of Financial Assistance', width: '50%' },
              { field: 'amount', label: 'Amount', width: '30%' }
            ]

            const countryRows = (() => {
              const rows = []

              if (countryItem.reliefCredits !== undefined && countryItem.reliefCredits !== null && countryItem.reliefCredits !== '') {
                rows.push({
                  type: 'Tax relief and tax credits',
                  amount: typeof countryItem.reliefCredits === 'number' ? countryItem.reliefCredits.toLocaleString() : countryItem.reliefCredits
                })
              }
              if (countryItem.subsidies !== undefined && countryItem.subsidies !== null && countryItem.subsidies !== '') {
                rows.push({
                  type: 'Subsidies',
                  amount: typeof countryItem.subsidies === 'number' ? countryItem.subsidies.toLocaleString() : countryItem.subsidies
                })
              }
              if (countryItem.holidays !== undefined && countryItem.holidays !== null && countryItem.holidays !== '') {
                rows.push({
                  type: 'Royalty Holidays',
                  amount: typeof countryItem.holidays === 'number' ? countryItem.holidays.toLocaleString() : countryItem.holidays
                })
              }
              if (countryItem.incentives !== undefined && countryItem.incentives !== null && countryItem.incentives !== '') {
                rows.push({
                  type: 'Financial incentives',
                  amount: typeof countryItem.incentives === 'number' ? countryItem.incentives.toLocaleString() : countryItem.incentives
                })
              }
              if (countryItem.ecas !== undefined && countryItem.ecas !== null && countryItem.ecas !== '') {
                rows.push({
                  type: 'Financial assistance from Export Credit Agencies (ECAs)',
                  amount: typeof countryItem.ecas === 'number' ? countryItem.ecas.toLocaleString() : countryItem.ecas
                })
              }
              if (countryItem.investmentgrant !== undefined && countryItem.investmentgrant !== null && countryItem.investmentgrant !== '') {
                rows.push({
                  type: 'Investment grants, research and development grants, and other relevant types of grant',
                  amount: typeof countryItem.investmentgrant === 'number' ? countryItem.investmentgrant.toLocaleString() : countryItem.investmentgrant
                })
              }
              if (countryItem.awards !== undefined && countryItem.awards !== null && countryItem.awards !== '') {
                rows.push({
                  type: 'Awards',
                  amount: typeof countryItem.awards === 'number' ? countryItem.awards.toLocaleString() : countryItem.awards
                })
              }
              if (countryItem.other !== undefined && countryItem.other !== null && countryItem.other !== '') {
                rows.push({
                  type: 'Other financial benefits received or receivable from any government for any operation',
                  amount: typeof countryItem.other === 'number' ? countryItem.other.toLocaleString() : countryItem.other
                })
              }

              return rows
            })()

            return countryRows.length > 0 ? (
              <div key={countryIndex} style={{ marginBottom: '32px' }}>
                <p className={reportStyles.questionAnswer} style={{ marginLeft: '16px', marginBottom: '16px' }}>
                  Country: {countryItem.country || 'N/A'} | Currency: {countryItem.currency || 'N/A'}
                </p>
                <StyledReportTable
                  columns={countryColumns}
                  rows={countryRows}
                  showSerialNumber={true}
                />
              </div>
            ) : null
          })
          : (
            <div className={reportStyles.questionsContainer}>
              <div className={reportStyles.questionBox}>
                <p className={reportStyles.questionAnswer}>
                  No country-wise data provided.
                </p>
              </div>
            </div>
          )}
      </div>

      {/* Subsection c: Shareholding structure */}
      <div className={reportStyles.consolidatingSection}>
        <p className={reportStyles.consolidatingTitle}>
          c. Whether, and the extent to which, any government is present in the shareholding structure
        </p>
        <div className={reportStyles.questionsContainer}>
          <div className={reportStyles.questionBox}>
            <p className={reportStyles.questionAnswer} style={{ marginBottom: '12px' }}>
              {data?.shareholdingstructure?.action === true || data?.shareholdingstructure?.action === "Yes"
                ? 'Yes'
                : data?.shareholdingstructure?.action === false || data?.shareholdingstructure?.action === "No"
                  ? 'No'
                  : 'No information provided.'}
            </p>
            {(data?.shareholdingstructure?.action === true || data?.shareholdingstructure?.action === "Yes") && data?.shareholdingstructure?.remark && (
              <p className={reportStyles.questionAnswer}>
                {data.shareholdingstructure.remark}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Section 202: Market Presence 2016 */}
      <h1 className={reportStyles.sectionHeading}>
        202 : Market Presence 2016
      </h1>

      {/* Section 202-1: Ratios of standard entry level wage by gender compared to local minimum wage */}
      <h1 className={reportStyles.mainHeading}>
        202-1 : Ratios of standard entry level wage by gender compared to local minimum wage
      </h1>

      <div className={reportStyles.consolidatingSection}>
        {/* Subsection a */}
        <p className={reportStyles.consolidatingTitle}>
          a. What is the ratio of entry-level wages by gender at significant locations of operation to the applicable minimum wage for employees compensated based on wages subject to minimum wage rules?
        </p>
        <div className={reportStyles.questionsContainer}>
          <div className={reportStyles.questionBox}>
            <p className={reportStyles.questionAnswer}>
              {data?.entryLevelWageRatioByGender?.entryLevelWageRatioByGender || 'No information provided.'}
            </p>
          </div>
        </div>

        {/* Subsection b */}
        <p className={reportStyles.consolidatingTitle}>
          b. Could you provide details on the steps taken to ensure that other workers (excluding employees) involved in the organization's activities and compensated based on wages subject to minimum wage rules are paid above the minimum wage?
        </p>
        <div className={reportStyles.questionsContainer}>
          <div className={reportStyles.questionBox}>
            <p className={reportStyles.questionAnswer}>
              {data?.entryLevelWageRatioByGender?.stepsToEnsureAboveMinimumWage || 'No information provided.'}
            </p>
          </div>
        </div>

        {/* Subsection c */}
        <p className={reportStyles.consolidatingTitle}>
          c. Are there any significant locations of operation where a local minimum wage is absent or variable, categorized by gender? If different minimum wages are applicable, please specify which minimum wage is being utilized.
        </p>
        <div className={reportStyles.questionsContainer}>
          <div className={reportStyles.questionBox}>
            <p className={reportStyles.questionAnswer}>
              {data?.entryLevelWageRatioByGender?.locationsWithNoOrVariableMinimumWage || 'No information provided.'}
            </p>
          </div>
        </div>

        {/* Subsection d */}
        <p className={reportStyles.consolidatingTitle}>
          d. How do you define 'significant locations of operation' within your organization's context?
        </p>
        <div className={reportStyles.questionsContainer}>
          <div className={reportStyles.questionBox}>
            <p className={reportStyles.questionAnswer}>
              {data?.entryLevelWageRatioByGender?.significantLocationsDefinition || 'No information provided.'}
            </p>
          </div>
        </div>
      </div>

      {/* Section 202-2: Proportion of senior management hired from the local community */}
      <h1 className={reportStyles.mainHeading}>
        202-2 : Proportion of senior management hired from the local community
      </h1>

      <div className={reportStyles.consolidatingSection}>
        {/* Subsection a */}
        <p className={reportStyles.consolidatingTitle}>
          a. What percentage of senior management positions at significant locations of operation are filled by individuals hired from the local community?
        </p>
        <div className={reportStyles.questionsContainer}>
          <div className={reportStyles.questionBox}>
            <p className={reportStyles.questionAnswer}>
              {data?.seniorManagementLocalHiresProportion?.percentageSeniorManagementLocalHires || 'No information provided.'}
            </p>
          </div>
        </div>

        {/* Subsection b */}
        <p className={reportStyles.consolidatingTitle}>
          b. How does your organization define 'senior management' positions?
        </p>
        <div className={reportStyles.questionsContainer}>
          <div className={reportStyles.questionBox}>
            <p className={reportStyles.questionAnswer}>
              {data?.seniorManagementLocalHiresProportion?.seniorManagementDefinition || 'No information provided.'}
            </p>
          </div>
        </div>

        {/* Subsection c */}
        <p className={reportStyles.consolidatingTitle}>
          c. How does your organization define the term 'local' in the context of geographical hiring practices?
        </p>
        <div className={reportStyles.questionsContainer}>
          <div className={reportStyles.questionBox}>
            <p className={reportStyles.questionAnswer}>
              {data?.seniorManagementLocalHiresProportion?.geographicalHiring || 'No information provided.'}
            </p>
          </div>
        </div>

        {/* Subsection d */}
        <p className={reportStyles.consolidatingTitle}>
          d. Could you provide the definition your organization uses for identifying 'significant locations of operation'?
        </p>
        <div className={reportStyles.questionsContainer}>
          <div className={reportStyles.questionBox}>
            <p className={reportStyles.questionAnswer}>
              {data?.seniorManagementLocalHiresProportion?.significantLocationsDefinition || 'No information provided.'}
            </p>
          </div>
        </div>
      </div>

      {/* Section 203: Indirect Economic Impact */}
      <h1 className={reportStyles.sectionHeading}>
        203 : Indirect Economic Impact
      </h1>

      {/* Section 203-1: Infrastructure investments and services supported */}
      <h1 className={reportStyles.mainHeading}>
        203-1 : Infrastructure investments and services supported
      </h1>

      {/* Subsection a: Financial assistance received by country */}
      <div className={reportStyles.consolidatingSection}>
        <p className={reportStyles.consolidatingTitle}>
          a. Financial assistance received by country
        </p>
        
        {(() => {
          const assistanceColumns = [
            { field: 'name', label: 'Name of Investment / Service', width: '30%' },
            { field: 'cost', label: 'Cost', width: '20%' },
            { field: 'size', label: 'Size', width: '20%' },
            { field: 'duration', label: 'Duration', width: '20%' }
          ]

          const assistanceRows = data?.finalcalAssistance && Array.isArray(data.finalcalAssistance) && data.finalcalAssistance.length > 0
            ? data.finalcalAssistance.map((item, index) => ({
                name: item.name || '-',
                cost: item.cost !== undefined && item.cost !== null && item.cost !== '' 
                  ? (typeof item.cost === 'number' ? item.cost.toLocaleString() : item.cost)
                  : '-',
                size: item.size !== undefined && item.size !== null && item.size !== ''
                  ? (typeof item.size === 'number' ? item.size.toLocaleString() : item.size)
                  : '-',
                duration: item.duration !== undefined && item.duration !== null && item.duration !== ''
                  ? (typeof item.duration === 'number' ? item.duration.toLocaleString() : item.duration)
                  : '-'
              }))
            : []

          return (
            <>
              {assistanceRows.length > 0 && (
                <p className={reportStyles.questionAnswer} style={{ marginLeft: '16px', marginBottom: '16px' }}>
                  Currency: {data?.finalcalAssistanceCurrency || 'N/A'}
                </p>
              )}
              <StyledReportTable
                columns={assistanceColumns}
                rows={assistanceRows}
                showSerialNumber={true}
              />
            </>
          )
        })()}
      </div>

      {/* Subsection b: Infrastructure impacts on communities */}
      <div className={reportStyles.consolidatingSection}>
        <p className={reportStyles.consolidatingTitle}>
          b. How do you anticipate or currently observe impacts on communities and local economies due to your infrastructure investments and services? Please include both positive and negative impacts where applicable
        </p>
        
        {(() => {
          const impactColumns = [
            { field: 'name', label: 'Name of Investment / Service', width: '25%' },
            { field: 'methodology', label: 'Methodology', width: '25%' },
            { field: 'positiveImpact', label: 'Positive Impact', width: '25%' },
            { field: 'negativeImpact', label: 'Negative Impact', width: '25%' }
          ]

          const impactRows = data?.infrastructureImpactOnCommunities && Array.isArray(data.infrastructureImpactOnCommunities) && data.infrastructureImpactOnCommunities.length > 0
            ? data.infrastructureImpactOnCommunities.map((item) => ({
                name: item.name || '-',
                methodology: item.methodology || '-',
                positiveImpact: item.positiveImpact || '-',
                negativeImpact: item.negativeImpact || '-'
              }))
            : []

          return (
            <StyledReportTable
              columns={impactColumns}
              rows={impactRows}
              showSerialNumber={true}
            />
          )
        })()}
      </div>

      {/* Subsection c: Investment and service type */}
      <div className={reportStyles.consolidatingSection}>
        <p className={reportStyles.consolidatingTitle}>
          c. Are the investments and services provided considered as commercial, in-kind, or pro bono engagements? Please specify for each.
        </p>
        
        {(() => {
          const typeColumns = [
            { field: 'name', label: 'Name of Investment / Service', width: '50%' },
            { field: 'type1', label: 'Type of engagement', width: '50%' }
          ]

          const typeRows = data?.investmentAndServiceType && Array.isArray(data.investmentAndServiceType) && data.investmentAndServiceType.length > 0
            ? data.investmentAndServiceType.map((item) => ({
                name: item.name || '-',
                type1: item.type1 || '-'
              }))
            : []

          return (
            <StyledReportTable
              columns={typeColumns}
              rows={typeRows}
              showSerialNumber={true}
            />
          )
        })()}
      </div>

      {/* Section 203-2: Significant indirect economic impacts */}
      <h1 className={reportStyles.mainHeading}>
        203-2 : Significant indirect economic impacts
      </h1>

      {/* Subsection a: Examples of indirect economic impacts */}
      <div className={reportStyles.consolidatingSection}>
        <p className={reportStyles.consolidatingTitle}>
          a. Provide examples of significant indirect economic impacts attributable to the organization, highlighting both positive and negative effects. This may include changes in productivity across organizations or sectors, economic development in impoverished areas, impacts of social or environmental conditions, accessibility of products and services for low-income individuals, employment effects within the supply or distribution chain, influences on foreign direct investment, changes resulting from operational or activity relocation, and economic outcomes from product or service utilization
        </p>
        
        {(() => {
          const impactColumns = [
            { field: 'impact', label: 'Significant indirect economic impacts attributable to the organization', width: '40%' },
            { field: 'positiveImpact', label: 'Positive Impact', width: '30%' },
            { field: 'negativeImpact', label: 'Negative Impact', width: '30%' }
          ]

          const impactRows = data?.examplesOfIndirectEconomicImpacts && Array.isArray(data.examplesOfIndirectEconomicImpacts) && data.examplesOfIndirectEconomicImpacts.length > 0
            ? data.examplesOfIndirectEconomicImpacts.map((item) => ({
                impact: item.impact || '-',
                positiveImpact: item.positiveImpact || '-',
                negativeImpact: item.negativeImpact || '-'
              }))
            : []

          return (
            <StyledReportTable
              columns={impactColumns}
              rows={impactRows}
              showSerialNumber={true}
            />
          )
        })()}
      </div>

      {/* Subsection b: Alignment with external benchmarks */}
      <div className={reportStyles.consolidatingSection}>
        <p className={reportStyles.consolidatingTitle}>
          b. In the context of external benchmarks and stakeholder priorities such as national and international standards, protocols, and policy agendas, please assess the significance of the indirect economic impacts generated by the organization. Consider the spectrum of indirect economic effects outlined in the guidance, and evaluate how these impacts align with established benchmarks and stakeholder priorities.
        </p>
        <div className={reportStyles.questionsContainer}>
          <div className={reportStyles.questionBox}>
            <p className={reportStyles.questionAnswer}>
              {data?.alignmentWithExternal || 'No information provided.'}
            </p>
          </div>
        </div>
      </div>

      {/* Section 204: Procurement Practices 2016 */}
      <h1 className={reportStyles.sectionHeading}>
        204 : Procurement Practices 2016
      </h1>

      {/* Section 204-1: Significant indirect economic impacts */}
      <h1 className={reportStyles.mainHeading}>
        204-1 : Significant indirect economic impacts
      </h1>

      {/* Subsection a: Procurement budget for local suppliers */}
      <div className={reportStyles.consolidatingSection}>
        <p className={reportStyles.consolidatingTitle}>
          a. What percentage of the procurement budget allocated for significant locations of operation is utilized for suppliers local to those specific operations, considering both products and services purchased locally?
        </p>
        
        {(() => {
          const procurementColumns = [
            { field: 'location', label: 'Location of Operation', width: '50%' },
            { field: 'budget', label: '% of Procurement Budget Utilized for Local Suppliers', width: '50%' }
          ]

          const procurementRows = data?.procurementPractices?.procurementBudgetLocalSuppliers && Array.isArray(data.procurementPractices.procurementBudgetLocalSuppliers) && data.procurementPractices.procurementBudgetLocalSuppliers.length > 0
            ? data.procurementPractices.procurementBudgetLocalSuppliers.map((item) => ({
                location: item.location || '-',
                budget: item.budget !== undefined && item.budget !== null && item.budget !== ''
                  ? `${item.budget}${item.budget.toString().includes('%') ? '' : '%'}`
                  : '-'
              }))
            : []

          return (
            <StyledReportTable
              columns={procurementColumns}
              rows={procurementRows}
              showSerialNumber={true}
            />
          )
        })()}
      </div>

      {/* Subsection b: Local geographical boundaries */}
      <div className={reportStyles.consolidatingSection}>
        <p className={reportStyles.consolidatingTitle}>
          b. How does your organization define 'local' in terms of geographical boundaries for procurement purposes?
        </p>
        <div className={reportStyles.questionsContainer}>
          <div className={reportStyles.questionBox}>
            <p className={reportStyles.questionAnswer}>
              {data?.procurementPractices?.localGeographicalBoundaries || 'No information provided.'}
            </p>
          </div>
        </div>
      </div>

      {/* Subsection c: Significant locations criteria */}
      <div className={reportStyles.consolidatingSection}>
        <p className={reportStyles.consolidatingTitle}>
          c. What criteria does your organization use to determine 'significant locations of operation' when assessing procurement data?
        </p>
        <div className={reportStyles.questionsContainer}>
          <div className={reportStyles.questionBox}>
            <p className={reportStyles.questionAnswer}>
              {data?.procurementPractices?.significantLocationsCriteria || 'No information provided.'}
            </p>
          </div>
        </div>
      </div>

      {/* Section 205: Anti-corruption 2016 */}
      <h1 className={reportStyles.sectionHeading}>
        205 : Anti-corruption 2016
      </h1>

      {/* Section 205-1: Operations assessed for risks related to corruption */}
      <h1 className={reportStyles.mainHeading}>
        205-1 : Operations assessed for risks related to corruption
      </h1>

      {/* Subsection a: Number of operations assessed */}
      <div className={reportStyles.consolidatingSection}>
        <p className={reportStyles.consolidatingTitle}>
          a. How many operations have undergone assessment for risks related to corruption, and what percentage does this represent of your total operations?
        </p>
        <div className={`${reportStyles.questionsContainer} ${reportStyles.sameLineGroup}`}>
          <div className={reportStyles.questionBox}>
            <div className={reportStyles.questionRow}>
              <span className={reportStyles.questionValue}>
                Number of operations assessed:
              </span>
              <span className={`${reportStyles.questionLabel} common_text_style`}>
                {data?.operationsAssessedForCorruption?.numberOfEmployees !== undefined && data?.operationsAssessedForCorruption?.numberOfEmployees !== null && data?.operationsAssessedForCorruption?.numberOfEmployees !== ''
                  ? (typeof data.operationsAssessedForCorruption.numberOfEmployees === 'number' 
                      ? data.operationsAssessedForCorruption.numberOfEmployees.toLocaleString() 
                      : data.operationsAssessedForCorruption.numberOfEmployees)
                  : 'No information provided.'}
              </span>
            </div>
          </div>
          <div className={reportStyles.questionBox}>
            <div className={reportStyles.questionRow}>
              <span className={reportStyles.questionValue}>
                Total operations:
              </span>
              <span className={`${reportStyles.questionLabel} common_text_style`}>
                {data?.operationsAssessedForCorruption?.total !== undefined && data?.operationsAssessedForCorruption?.total !== null && data?.operationsAssessedForCorruption?.total !== ''
                  ? (typeof data.operationsAssessedForCorruption.total === 'number' 
                      ? data.operationsAssessedForCorruption.total.toLocaleString() 
                      : data.operationsAssessedForCorruption.total)
                  : 'No information provided.'}
              </span>
            </div>
          </div>
          <div className={reportStyles.questionBox}>
            <div className={reportStyles.questionRow}>
              <span className={reportStyles.questionValue}>
                Percentage:
              </span>
              <span className={`${reportStyles.questionLabel} common_text_style`}>
                {data?.operationsAssessedForCorruption?.percentage !== undefined && data?.operationsAssessedForCorruption?.percentage !== null && data?.operationsAssessedForCorruption?.percentage !== ''
                  ? `${data.operationsAssessedForCorruption.percentage}${data.operationsAssessedForCorruption.percentage.toString().includes('%') ? '' : '%'}`
                  : 'No information provided.'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Subsection b: Significant corruption risks */}
      <div className={reportStyles.consolidatingSection}>
        <p className={reportStyles.consolidatingTitle}>
          b. Identify the significant risks related to corruption that have been identified through the risk assessment process?
        </p>
        
        {(() => {
          const riskColumns = [
            { field: 'riskCorruption', label: 'Risks related to corruption', width: '100%' }
          ]

          const riskRows = data?.significantCorruptionRisks && Array.isArray(data.significantCorruptionRisks) && data.significantCorruptionRisks.length > 0
            ? data.significantCorruptionRisks.map((item) => ({
                riskCorruption: item.riskCorruption || '-'
              }))
            : []

          return (
            <StyledReportTable
              columns={riskColumns}
              rows={riskRows}
              showSerialNumber={true}
            />
          )
        })()}
      </div>

      {/* Section 205-2: Communication and training about anti-corruption policies and procedures */}
      <h1 className={reportStyles.mainHeading}>
        205-2 : Communication and training about anti-corruption policies and procedures
      </h1>

      {/* Subsection a: Governance body communication */}
      <div className={reportStyles.consolidatingSection}>
        <p className={reportStyles.consolidatingTitle}>
          a. How many members of the governance body have the organization's anti-corruption policies and procedures been communicated to, and what percentage does this represent, broken down by region?
        </p>
        
        {(() => {
          const govColumns = [
            { field: 'location', label: 'Region of Operation', width: '25%' },
            { field: 'goveranceBody', label: 'Total Members of Governance Body', width: '25%' },
            { field: 'antiCorruption', label: 'Members communicated about Anti-Corruption Policies', width: '30%' },
            { field: 'infored', label: '% Informed', width: '20%' }
          ]

          const govRows = data?.brokenDownByRegion && Array.isArray(data.brokenDownByRegion) && data.brokenDownByRegion.length > 0
            ? data.brokenDownByRegion.map((item) => ({
                location: item.location || '-',
                goveranceBody: item.goveranceBody !== undefined && item.goveranceBody !== null && item.goveranceBody !== ''
                  ? (typeof item.goveranceBody === 'number' ? item.goveranceBody.toLocaleString() : item.goveranceBody)
                  : '-',
                antiCorruption: item.antiCorruption !== undefined && item.antiCorruption !== null && item.antiCorruption !== ''
                  ? (typeof item.antiCorruption === 'number' ? item.antiCorruption.toLocaleString() : item.antiCorruption)
                  : '-',
                infored: item.infored !== undefined && item.infored !== null && item.infored !== ''
                  ? `${item.infored}${item.infored.toString().includes('%') ? '' : '%'}`
                  : '-'
              }))
            : []

          return (
            <StyledReportTable
              columns={govColumns}
              rows={govRows}
              showSerialNumber={true}
            />
          )
        })()}
      </div>

      {/* Subsection b: Employee communication */}
      <div className={reportStyles.consolidatingSection}>
        <p className={reportStyles.consolidatingTitle}>
          b. How many employees have the organization's anti-corruption policies and procedures been communicated to, and what percentage does this represent, broken down by employee category and region?
        </p>
        
        {(() => {
          const employeeData = data?.representBrokenDownByEmployee || []
          
          if (employeeData.length > 0) {
            return employeeData.map((regionData, regionIndex) => {
              const empColumns = [
                { field: 'employeeCategory', label: 'Employee Category', width: '25%' },
                { field: 'totalCategory', label: 'Total No. of Employees in Category', width: '25%' },
                { field: 'antiCorruptionPolicies', label: 'No. of Employees Communicated about Anti-Corruption policies', width: '28%' },
                { field: 'percentage', label: '% Employees Communicated', width: '22%' }
              ]

              const empRows = regionData?.table && Array.isArray(regionData.table) && regionData.table.length > 0
                ? regionData.table.map((item) => ({
                    employeeCategory: item.employeeCategory || '-',
                    totalCategory: item.totalCategory !== undefined && item.totalCategory !== null && item.totalCategory !== ''
                      ? (typeof item.totalCategory === 'number' ? item.totalCategory.toLocaleString() : item.totalCategory)
                      : '-',
                    antiCorruptionPolicies: item.antiCorruptionPolicies !== undefined && item.antiCorruptionPolicies !== null && item.antiCorruptionPolicies !== ''
                      ? (typeof item.antiCorruptionPolicies === 'number' ? item.antiCorruptionPolicies.toLocaleString() : item.antiCorruptionPolicies)
                      : '-',
                    percentage: item.percentage !== undefined && item.percentage !== null && item.percentage !== ''
                      ? `${item.percentage}${item.percentage.toString().includes('%') ? '' : '%'}`
                      : '-'
                  }))
                : []

              return (
                <div key={regionIndex} style={{ marginBottom: '24px' }}>
                  {regionData.region && (
                    <p style={{ fontWeight: 'bold', marginBottom: '12px', fontSize: '16px' }}>
                      Region: {regionData.region}
                    </p>
                  )}
                  <StyledReportTable
                    columns={empColumns}
                    rows={empRows}
                    showSerialNumber={true}
                  />
                </div>
              )
            })
          }
        })()}
      </div>

      {/* Subsection c: Business partner communication */}
      <div className={reportStyles.consolidatingSection}>
        <p className={reportStyles.consolidatingTitle}>
          c. How many business partners have the organization's anti-corruption policies and procedures been communicated to, and what percentage does this represent, broken down by business partner category and region?
        </p>
        
        {(() => {
          const partnerData = data?.commAntiCorruptionPolicies || []
          
          if (partnerData.length > 0) {
            return partnerData.map((regionData, regionIndex) => {
              const partnerColumns = [
                { field: 'typeOfBusiness', label: 'Type of Business Partner', width: '20%' },
                { field: 'totalBusinessPartners', label: 'Total no. of Business partners', width: '18%' },
                { field: 'noOfCategory', label: 'No. of Business Partner Communicated about Anti-Corruption policies', width: '22%' },
                { field: 'percentage', label: '% Business Partner Informed', width: '18%' },
                { field: 'antiCorruptionPolicies', label: 'Additional Persons/ Organizations informed', width: '22%' }
              ]

              const partnerRows = regionData?.rows && Array.isArray(regionData.rows) && regionData.rows.length > 0
                ? regionData.rows.map((item) => ({
                    typeOfBusiness: item.typeOfBusiness || '-',
                    totalBusinessPartners: item.totalBusinessPartners !== undefined && item.totalBusinessPartners !== null && item.totalBusinessPartners !== ''
                      ? (typeof item.totalBusinessPartners === 'number' ? item.totalBusinessPartners.toLocaleString() : item.totalBusinessPartners)
                      : '-',
                    noOfCategory: item.noOfCategory !== undefined && item.noOfCategory !== null && item.noOfCategory !== ''
                      ? (typeof item.noOfCategory === 'number' ? item.noOfCategory.toLocaleString() : item.noOfCategory)
                      : '-',
                    percentage: item.percentage !== undefined && item.percentage !== null && item.percentage !== ''
                      ? `${item.percentage}${item.percentage.toString().includes('%') ? '' : '%'}`
                      : '-',
                    antiCorruptionPolicies: item.antiCorruptionPolicies !== undefined && item.antiCorruptionPolicies !== null && item.antiCorruptionPolicies !== ''
                      ? (typeof item.antiCorruptionPolicies === 'number' ? item.antiCorruptionPolicies.toLocaleString() : item.antiCorruptionPolicies)
                      : '-'
                  }))
                : []

              return (
                <div key={regionIndex} style={{ marginBottom: '24px' }}>
                  {regionData.region && (
                    <p style={{ fontWeight: 'bold', marginBottom: '12px', fontSize: '16px' }}>
                      Region: {regionData.region}
                    </p>
                  )}
                  <StyledReportTable
                    columns={partnerColumns}
                    rows={partnerRows}
                    showSerialNumber={true}
                  />
                </div>
              )
            })
          }
        })()}
      </div>

      {/* Subsection d: Governance body training */}
      <div className={reportStyles.consolidatingSection}>
        <p className={reportStyles.consolidatingTitle}>
          d. How many members of the governance body have received training on anti-corruption, and what percentage does this represent, broken down by region?
        </p>
        
        {(() => {
          const trainingGovColumns = [
            { field: 'region', label: 'Region', width: '25%' },
            { field: 'totalGovernaceBody', label: 'Total number of governance body members', width: '25%' },
            { field: 'governaceBody', label: 'Number of Governance Body Members Trained', width: '30%' },
            { field: 'percentage', label: '% of Members Trained', width: '20%' }
          ]

          const trainingGovRows = data?.trainingGovernanceBodyAntiCorruption && Array.isArray(data.trainingGovernanceBodyAntiCorruption) && data.trainingGovernanceBodyAntiCorruption.length > 0
            ? data.trainingGovernanceBodyAntiCorruption.map((item) => ({
                region: item.region || '-',
                totalGovernaceBody: item.totalGovernaceBody !== undefined && item.totalGovernaceBody !== null && item.totalGovernaceBody !== ''
                  ? (typeof item.totalGovernaceBody === 'number' ? item.totalGovernaceBody.toLocaleString() : item.totalGovernaceBody)
                  : '-',
                governaceBody: item.governaceBody !== undefined && item.governaceBody !== null && item.governaceBody !== ''
                  ? (typeof item.governaceBody === 'number' ? item.governaceBody.toLocaleString() : item.governaceBody)
                  : '-',
                percentage: item.percentage !== undefined && item.percentage !== null && item.percentage !== ''
                  ? `${item.percentage}${item.percentage.toString().includes('%') ? '' : '%'}`
                  : '-'
              }))
            : []

          return (
            <StyledReportTable
              columns={trainingGovColumns}
              rows={trainingGovRows}
              showSerialNumber={true}
            />
          )
        })()}
      </div>

      {/* Subsection e: Employee training */}
      <div className={reportStyles.consolidatingSection}>
        <p className={reportStyles.consolidatingTitle}>
          e. How many employees have received training on anti-corruption, and what percentage does this represent, broken down by employee category and region?
        </p>
        
        {(() => {
          const trainingEmpData = data?.trainingEmployeesAntiCorruption || []
          
          if (trainingEmpData.length > 0) {
            return trainingEmpData.map((regionData, regionIndex) => {
              const trainingEmpColumns = [
                { field: 'employeeCategory', label: 'Employee Category', width: '25%' },
                { field: 'totalCategory', label: 'No. of Employees', width: '25%' },
                { field: 'antiCorruptionPolicies', label: 'Employees Trained on Anti-Corruption', width: '30%' },
                { field: 'percentage', label: '% of Employees Trained', width: '20%' }
              ]

              const trainingEmpRows = regionData?.table && Array.isArray(regionData.table) && regionData.table.length > 0
                ? regionData.table.map((item) => ({
                    employeeCategory: item.employeeCategory || '-',
                    totalCategory: item.totalCategory !== undefined && item.totalCategory !== null && item.totalCategory !== ''
                      ? (typeof item.totalCategory === 'number' ? item.totalCategory.toLocaleString() : item.totalCategory)
                      : '-',
                    antiCorruptionPolicies: item.antiCorruptionPolicies !== undefined && item.antiCorruptionPolicies !== null && item.antiCorruptionPolicies !== ''
                      ? (typeof item.antiCorruptionPolicies === 'number' ? item.antiCorruptionPolicies.toLocaleString() : item.antiCorruptionPolicies)
                      : '-',
                    percentage: item.percentage !== undefined && item.percentage !== null && item.percentage !== ''
                      ? `${item.percentage}${item.percentage.toString().includes('%') ? '' : '%'}`
                      : '-'
                  }))
                : []

              return (
                <div key={regionIndex} style={{ marginBottom: '24px' }}>
                  {regionData.region && (
                    <p style={{ fontWeight: 'bold', marginBottom: '12px', fontSize: '16px' }}>
                      Region: {regionData.region}
                    </p>
                  )}
                  <StyledReportTable
                    columns={trainingEmpColumns}
                    rows={trainingEmpRows}
                    showSerialNumber={true}
                  />
                </div>
              )
            })
          }
        })()}
      </div>

      {/* Section 205-3: Confirmed incidents of corruption and actions taken */}
      <h1 className={reportStyles.mainHeading}>
        205-3 : Confirmed incidents of corruption and actions taken
      </h1>

      {/* Subsection a: Confirmed incidents of corruption */}
      <div className={reportStyles.consolidatingSection}>
        <p className={reportStyles.consolidatingTitle}>
          a. How many confirmed incidents of corruption occurred during the reporting period, and what was the nature of these incidents? What was the outcome of such cases?
        </p>
        <div className={reportStyles.questionsContainer}>
          <div className={reportStyles.questionBox}>
            <p className={reportStyles.questionAnswer}>
              {data?.confirmedIncidentsOfCorruption !== undefined && data?.confirmedIncidentsOfCorruption !== null && data?.confirmedIncidentsOfCorruption !== ''
                ? data.confirmedIncidentsOfCorruption
                : 'No information provided.'}
            </p>
          </div>
        </div>
      </div>

      {/* Subsection b: Disciplinary actions */}
      <div className={reportStyles.consolidatingSection}>
        <p className={reportStyles.consolidatingTitle}>
          b. How many confirmed incidents of corruption resulted in the dismissal or disciplinary action against employees?
        </p>
        <div className={reportStyles.questionsContainer}>
          <div className={reportStyles.questionBox}>
            <p className={reportStyles.questionAnswer}>
              {data?.corruptionDisciplinaryActions !== undefined && data?.corruptionDisciplinaryActions !== null && data?.corruptionDisciplinaryActions !== ''
                ? data.corruptionDisciplinaryActions
                : 'No information provided.'}
            </p>
          </div>
        </div>
      </div>

      {/* Subsection c: Terminations and non-renewals */}
      <div className={reportStyles.consolidatingSection}>
        <p className={reportStyles.consolidatingTitle}>
          c. How many employees have the organization's anti-corruption policies and procedures been communicated to, and what percentage does this represent, broken down by employee category and region?
        </p>
        
        {(() => {
          const terminationColumns = [
            { field: 'typeOfBusiness', label: 'Type of Business Partner', width: '50%' },
            { field: 'nonRenewalContracts', label: 'Number of Incidents Leading to Termination or Non-Renewal of Contracts', width: '50%' }
          ]

          const terminationRows = data?.corruptionTerminationsPartnerships && Array.isArray(data.corruptionTerminationsPartnerships) && data.corruptionTerminationsPartnerships.length > 0
            ? data.corruptionTerminationsPartnerships.map((item) => ({
                typeOfBusiness: item.typeOfBusiness || '-',
                nonRenewalContracts: item.nonRenewalContracts !== undefined && item.nonRenewalContracts !== null && item.nonRenewalContracts !== ''
                  ? (typeof item.nonRenewalContracts === 'number' ? item.nonRenewalContracts.toLocaleString() : item.nonRenewalContracts)
                  : '-'
              }))
            : []

          return (
            <StyledReportTable
              columns={terminationColumns}
              rows={terminationRows}
              showSerialNumber={true}
            />
          )
        })()}
      </div>

      {/* Subsection d: Public legal cases */}
      <div className={reportStyles.consolidatingSection}>
        <p className={reportStyles.consolidatingTitle}>
          d. Report details of any public legal cases concerning corruption brought against the organization or its employees during the reporting period, including case status and outcomes.
        </p>
        <div className={reportStyles.questionsContainer}>
          <div className={reportStyles.questionBox}>
            <p className={reportStyles.questionAnswer}>
              {data?.publicLegalCasesCorruption !== undefined && data?.publicLegalCasesCorruption !== null && data?.publicLegalCasesCorruption !== ''
                ? data.publicLegalCasesCorruption
                : 'No information provided.'}
            </p>
          </div>
        </div>
      </div>

      {/* Section 206: Anti-competitive Behaviour 2016 */}
      <h1 className={reportStyles.sectionHeading}>
        206 : Anti-competitive Behaviour 2016
      </h1>

      {/* Section 206-1: Legal actions for anti-competitive behavior, anti-trust, and monopoly practices */}
      <h1 className={reportStyles.mainHeading}>
        206-1 : Legal actions for anti-competitive behavior, anti-trust, and monopoly practices
      </h1>

      {/* Subsection a: Number of legal actions */}
      <div className={reportStyles.consolidatingSection}>
        <p className={reportStyles.consolidatingTitle}>
          a. How many legal actions, either pending or completed during the reporting period, involve allegations of anti-competitive behavior and violations of anti-trust and monopoly legislation, with the organization identified as a participant?
        </p>
        <div className={`${reportStyles.questionsContainer} ${reportStyles.sameLineGroup}`}>
          <div className={reportStyles.questionBox}>
            <div className={reportStyles.questionRow}>
              <span className={reportStyles.questionValue}>
                Pending:
              </span>
              <span className={`${reportStyles.questionLabel} common_text_style`}>
                {data?.antiCompetitiveBehavior?.numberOfLegalAction?.pending !== undefined &&
                data?.antiCompetitiveBehavior?.numberOfLegalAction?.pending !== null &&
                data?.antiCompetitiveBehavior?.numberOfLegalAction?.pending !== ''
                  ? (typeof data.antiCompetitiveBehavior.numberOfLegalAction.pending === 'number'
                      ? data.antiCompetitiveBehavior.numberOfLegalAction.pending.toLocaleString()
                      : data.antiCompetitiveBehavior.numberOfLegalAction.pending)
                  : 'No information provided.'}
              </span>
            </div>
          </div>
          <div className={reportStyles.questionBox}>
            <div className={reportStyles.questionRow}>
              <span className={reportStyles.questionValue}>
                Completed:
              </span>
              <span className={`${reportStyles.questionLabel} common_text_style`}>
                {data?.antiCompetitiveBehavior?.numberOfLegalAction?.completed !== undefined &&
                data?.antiCompetitiveBehavior?.numberOfLegalAction?.completed !== null &&
                data?.antiCompetitiveBehavior?.numberOfLegalAction?.completed !== ''
                  ? (typeof data.antiCompetitiveBehavior.numberOfLegalAction.completed === 'number'
                      ? data.antiCompetitiveBehavior.numberOfLegalAction.completed.toLocaleString()
                      : data.antiCompetitiveBehavior.numberOfLegalAction.completed)
                  : 'No information provided.'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Subsection b: Legal outcomes */}
      <div className={reportStyles.consolidatingSection}>
        <p className={reportStyles.consolidatingTitle}>
          b. What are the main outcomes of the legal actions that have been completed during the reporting period, including any decisions or judgments?
        </p>
        
        {(() => {
          const outcomeColumns = [
            { field: 'legalAction', label: 'Legal Action Taken', width: '50%' },
            { field: 'outCome', label: 'Main Outcome', width: '50%' }
          ]

          const outcomeRows = data?.antiCompetitiveBehavior?.legalOutcomes && Array.isArray(data.antiCompetitiveBehavior.legalOutcomes) && data.antiCompetitiveBehavior.legalOutcomes.length > 0
            ? data.antiCompetitiveBehavior.legalOutcomes.map((item) => ({
                legalAction: item.legalAction || '-',
                outCome: item.outCome || '-'
              }))
            : []

          return (
            <StyledReportTable
              columns={outcomeColumns}
              rows={outcomeRows}
              showSerialNumber={true}
            />
          )
        })()}
      </div>

      {/* Section 207: Tax 2019 */}
      <h1 className={reportStyles.sectionHeading}>
        207 : Tax 2019
      </h1>

      {/* Section 207-1: Approach to tax */}
      <h1 className={reportStyles.mainHeading}>
        207-1 : Approach to tax
      </h1>

      {/* Subsection a: Tax strategy */}
      <div className={reportStyles.consolidatingSection}>
        <p className={reportStyles.consolidatingTitle}>
          a. Does the organization have a tax strategy, and if so, can you provide a summary or abstract of this strategy if it's not publicly available? Additionally, if the strategy applies to a smaller number of entities or tax jurisdictions than reported, please list the entities or tax jurisdictions to which it applies.
        </p>
        <div className={reportStyles.questionsContainer}>
          <div className={reportStyles.questionBox}>
            <div className={reportStyles.questionRow}>
              <p className={reportStyles.questionLabel}>Does the organization have a tax strategy?</p>
              <p className={reportStyles.questionValue}>
                {data?.apporachToTax?.taxStrategySummary?.status !== undefined && data?.apporachToTax?.taxStrategySummary?.status !== null
                  ? (data.apporachToTax.taxStrategySummary.status ? 'Yes' : 'No')
                  : 'No information provided.'}
              </p>
            </div>
            {data?.apporachToTax?.taxStrategySummary?.status && (
              <div className={reportStyles.questionRow} style={{ marginTop: '12px' }}>
                <p className={reportStyles.questionLabel}>Summary or abstract:</p>
                <p className={reportStyles.questionAnswer}>
                  {data?.apporachToTax?.taxStrategySummary?.remarks !== undefined && data?.apporachToTax?.taxStrategySummary?.remarks !== null && data?.apporachToTax?.taxStrategySummary?.remarks !== ''
                    ? data.apporachToTax.taxStrategySummary.remarks
                    : 'No information provided.'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Subsection b: Tax strategy approval body */}
      <div className={reportStyles.consolidatingSection}>
        <p className={reportStyles.consolidatingTitle}>
          b. Which governance body or executive-level position within the organization formally reviews and approves the tax strategy, and how often is this review conducted?
        </p>
        <div className={reportStyles.questionsContainer}>
          <div className={reportStyles.questionBox}>
            <p className={reportStyles.questionAnswer}>
              {data?.apporachToTax?.taxStrategyApprovalBody !== undefined && data?.apporachToTax?.taxStrategyApprovalBody !== null && data?.apporachToTax?.taxStrategyApprovalBody !== ''
                ? data.apporachToTax.taxStrategyApprovalBody
                : 'No information provided.'}
            </p>
          </div>
        </div>
      </div>

      {/* Subsection c: Regulatory compliance approach */}
      <div className={reportStyles.consolidatingSection}>
        <p className={reportStyles.consolidatingTitle}>
          c. Describe the organization's approach to regulatory compliance, including any statements in the tax strategy regarding its intention with respect to tax laws in the jurisdictions where it operates. Specifically, does the organization seek to comply with the letter and the spirit of the law, taking reasonable steps to determine and follow the intention of the legislature?
        </p>
        <div className={reportStyles.questionsContainer}>
          <div className={reportStyles.questionBox}>
            <p className={reportStyles.questionAnswer}>
              {data?.apporachToTax?.regulatoryComplianceApproach !== undefined && data?.apporachToTax?.regulatoryComplianceApproach !== null && data?.apporachToTax?.regulatoryComplianceApproach !== ''
                ? data.apporachToTax.regulatoryComplianceApproach
                : 'No information provided.'}
            </p>
          </div>
        </div>
      </div>

      {/* Subsection d: Tax alignment with business and sustainability */}
      <div className={reportStyles.consolidatingSection}>
        <p className={reportStyles.consolidatingTitle}>
          d. How is the organization's approach to tax linked to its business and sustainable development strategies? Explain how its tax planning aligns with its commercial activities and any relevant statements from its tax strategy regarding alignment with sustainable development commitments in the jurisdictions where it operates, including consideration of economic and social impacts.
        </p>
        <div className={reportStyles.questionsContainer}>
          <div className={reportStyles.questionBox}>
            <p className={reportStyles.questionAnswer}>
              {data?.apporachToTax?.taxAlignmentWithBusinessAndSustainability !== undefined && data?.apporachToTax?.taxAlignmentWithBusinessAndSustainability !== null && data?.apporachToTax?.taxAlignmentWithBusinessAndSustainability !== ''
                ? data.apporachToTax.taxAlignmentWithBusinessAndSustainability
                : 'No information provided.'}
            </p>
          </div>
        </div>
      </div>

      {/* Section 207-2: Tax governance, control, and risk management */}
      <h1 className={reportStyles.mainHeading}>
        207-2 : Tax governance, control, and risk management
      </h1>

      {/* Subsection a: Tax Governance and Control Framework */}
      <div className={reportStyles.consolidatingSection}>
        <p className={reportStyles.consolidatingTitle}>
          a. Tax Governance and Control Framework
        </p>

        {/* Subsection a.i */}
        <div className={reportStyles.questionsContainer} style={{ marginTop: '12px' }}>
          <div className={reportStyles.questionBox}>
            <p className={reportStyles.consolidatingTitle} style={{ marginBottom: '8px', fontSize: '16px' }}>
              i. Which governance body or executive-level position within the organization is accountable for compliance with the tax strategy, and to what extent does the highest governance body oversee compliance?
            </p>
            <p className={reportStyles.questionAnswer}>
              {data?.taxGovernace?.taxGovernanceAccountability !== undefined && data?.taxGovernace?.taxGovernanceAccountability !== null && data?.taxGovernace?.taxGovernanceAccountability !== ''
                ? data.taxGovernace.taxGovernanceAccountability
                : 'No information provided.'}
            </p>
          </div>
        </div>

        {/* Subsection a.ii */}
        <div className={reportStyles.questionsContainer} style={{ marginTop: '12px' }}>
          <div className={reportStyles.questionBox}>
            <p className={reportStyles.consolidatingTitle} style={{ marginBottom: '8px', fontSize: '16px' }}>
              ii. How is the approach to tax embedded within the organization, and can you provide examples of initiatives or programs supporting adherence to the tax strategy, such as training, remuneration schemes, or participation in tax transparency initiatives?
            </p>
            <p className={reportStyles.questionAnswer}>
              {data?.taxGovernace?.taxStrategyEmbeddingInitiatives !== undefined && data?.taxGovernace?.taxStrategyEmbeddingInitiatives !== null && data?.taxGovernace?.taxStrategyEmbeddingInitiatives !== ''
                ? data.taxGovernace.taxStrategyEmbeddingInitiatives
                : 'No information provided.'}
            </p>
          </div>
        </div>

        {/* Subsection a.iii */}
        <div className={reportStyles.questionsContainer} style={{ marginTop: '12px' }}>
          <div className={reportStyles.questionBox}>
            <p className={reportStyles.consolidatingTitle} style={{ marginBottom: '8px', fontSize: '16px' }}>
              iii. Describe the approach to tax risks, including examples of how risks are identified, managed, and monitored, considering factors such as compliance, uncertain tax positions, changes in legislation, and the organization's risk appetite and tolerance.
            </p>
            <p className={reportStyles.questionAnswer}>
              {data?.taxGovernace?.taxRiskManagementApproach !== undefined && data?.taxGovernace?.taxRiskManagementApproach !== null && data?.taxGovernace?.taxRiskManagementApproach !== ''
                ? data.taxGovernace.taxRiskManagementApproach
                : 'No information provided.'}
            </p>
          </div>
        </div>

        {/* Subsection a.iv */}
        <div className={reportStyles.questionsContainer} style={{ marginTop: '12px' }}>
          <div className={reportStyles.questionBox}>
            <p className={reportStyles.consolidatingTitle} style={{ marginBottom: '8px', fontSize: '16px' }}>
              iv. How is compliance with the tax governance and control framework evaluated, and to what extent does the highest governance body have oversight of its design, implementation, and effectiveness?
            </p>
            <p className={reportStyles.questionAnswer}>
              {data?.taxGovernace?.taxGovernanceFrameworkEvaluation !== undefined && data?.taxGovernace?.taxGovernanceFrameworkEvaluation !== null && data?.taxGovernace?.taxGovernanceFrameworkEvaluation !== ''
                ? data.taxGovernace.taxGovernanceFrameworkEvaluation
                : 'No information provided.'}
            </p>
          </div>
        </div>
      </div>

      {/* Subsection b: Mechanisms for raising concerns */}
      <div className={reportStyles.consolidatingSection}>
        <p className={reportStyles.consolidatingTitle}>
          b. Describe the mechanisms available for individuals to raise concerns about the organization's business conduct and integrity in relation to tax, such as whistleblowing procedures.
        </p>
        <div className={reportStyles.questionsContainer}>
          <div className={reportStyles.questionBox}>
            <p className={reportStyles.questionAnswer}>
              {data?.taxGovernace?.taxConcernsMechanisms !== undefined && data?.taxGovernace?.taxConcernsMechanisms !== null && data?.taxGovernace?.taxConcernsMechanisms !== ''
                ? data.taxGovernace.taxConcernsMechanisms
                : 'No information provided.'}
            </p>
          </div>
        </div>
      </div>

      {/* Subsection c: Assurance process */}
      <div className={reportStyles.consolidatingSection}>
        <p className={reportStyles.consolidatingTitle}>
          c. Provide a description of the assurance process for disclosures on tax, including any external assurance reports or statements. If applicable, please provide a link or reference to the external assurance report(s) or statement(s) covering the tax disclosures.
        </p>
        <div className={reportStyles.questionsContainer}>
          <div className={reportStyles.questionBox}>
            <p className={reportStyles.questionAnswer}>
              {data?.taxGovernace?.taxDisclosureAssuranceProcess !== undefined && data?.taxGovernace?.taxDisclosureAssuranceProcess !== null && data?.taxGovernace?.taxDisclosureAssuranceProcess !== ''
                ? data.taxGovernace.taxDisclosureAssuranceProcess
                : 'No information provided.'}
            </p>
          </div>
        </div>
      </div>

      {/* Section 207-3: Stakeholder engagement and management of concerns related to tax */}
      <h1 className={reportStyles.mainHeading}>
        207-3 : Stakeholder engagement and management of concerns related to tax
      </h1>

      {/* Subsection a: Approach to Stakeholder Engagement and Management of Stakeholder Concerns Related to Tax */}
      <div className={reportStyles.consolidatingSection}>
        <p className={reportStyles.consolidatingTitle}>
          a. Approach to Stakeholder Engagement and Management of Stakeholder Concerns Related to Tax:
        </p>

        {/* Subsection a.i */}
        <div className={reportStyles.questionsContainer} style={{ marginTop: '12px' }}>
          <div className={reportStyles.questionBox}>
            <p className={reportStyles.consolidatingTitle} style={{ marginBottom: '8px', fontSize: '16px' }}>
              i. Describe the organization's approach to engagement with tax authorities, including any participation in cooperative compliance agreements or seeking advance pricing agreements.
            </p>
            <p className={reportStyles.questionAnswer}>
              {data?.stackeHoder?.taxAuthoritiesEngagement !== undefined && data?.stackeHoder?.taxAuthoritiesEngagement !== null && data?.stackeHoder?.taxAuthoritiesEngagement !== ''
                ? data.stackeHoder.taxAuthoritiesEngagement
                : 'No information provided.'}
            </p>
          </div>
        </div>

        {/* Subsection a.ii */}
        <div className={reportStyles.questionsContainer} style={{ marginTop: '12px' }}>
          <div className={reportStyles.questionBox}>
            <p className={reportStyles.consolidatingTitle} style={{ marginBottom: '8px', fontSize: '16px' }}>
              ii. Explain the organization's approach to public policy advocacy on tax, including any lobbying activities, stance on significant tax issues, and involvement with representative associations or committees engaged in tax-related public policy advocacy.
            </p>
            <p className={reportStyles.questionAnswer}>
              {data?.stackeHoder?.taxPublicPolicyAdvocacy !== undefined && data?.stackeHoder?.taxPublicPolicyAdvocacy !== null && data?.stackeHoder?.taxPublicPolicyAdvocacy !== ''
                ? data.stackeHoder.taxPublicPolicyAdvocacy
                : 'No information provided.'}
            </p>
          </div>
        </div>

        {/* Subsection a.iii */}
        <div className={reportStyles.questionsContainer} style={{ marginTop: '12px' }}>
          <div className={reportStyles.questionBox}>
            <p className={reportStyles.consolidatingTitle} style={{ marginBottom: '8px', fontSize: '16px' }}>
              iii. Outline the processes for collecting and considering the views and concerns of stakeholders, including external stakeholders, and provide examples of how stakeholder feedback has influenced the organization's approach to tax, tax strategy, or tax practices.
            </p>
            <p className={reportStyles.questionAnswer}>
              {data?.stackeHoder?.stakeholderFeedback !== undefined && data?.stackeHoder?.stakeholderFeedback !== null && data?.stackeHoder?.stakeholderFeedback !== ''
                ? data.stackeHoder.stakeholderFeedback
                : 'No information provided.'}
            </p>
          </div>
        </div>
      </div>

      {/* Section 207-4: Country-by-country reporting */}
      <h1 className={reportStyles.mainHeading}>
        207-4 : Country-by-country reporting
      </h1>

      {/* Subsection a: Tax jurisdictions list */}
      <div className={reportStyles.consolidatingSection}>
        <p className={reportStyles.consolidatingTitle}>
          a. Provide the list of all tax jurisdictions where the entities included in the organization's audited consolidated financial statements or filed financial information are resident for tax purposes.
        </p>
        
        {(() => {
          const jurisdictionColumns = [
            { field: 'tax', label: 'Tax Jurisdictions', width: '100%' }
          ]

          const jurisdictionRows = data?.countryReporting?.taxJurisdictionsList && Array.isArray(data.countryReporting.taxJurisdictionsList) && data.countryReporting.taxJurisdictionsList.length > 0
            ? data.countryReporting.taxJurisdictionsList.map((item) => ({
                tax: item.tax || '-'
              }))
            : []

          return (
            <StyledReportTable
              columns={jurisdictionColumns}
              rows={jurisdictionRows}
              showSerialNumber={true}
            />
          )
        })()}
      </div>

      {/* Subsection b: For each tax jurisdiction */}
      <div className={reportStyles.consolidatingSection}>
        <p className={reportStyles.consolidatingTitle}>
          b. For each tax jurisdiction listed in (a):
        </p>

        {/* Subsection b.i: Resident entities names */}
        <div style={{ marginTop: '24px' }}>
          <p className={reportStyles.consolidatingTitle} style={{ marginBottom: '12px', fontSize: '16px' }}>
            i. Provide the names of the resident entities, specifying if any are dormant.
          </p>
          {(() => {
            const columns = [
              { field: 'tax', label: 'Tax Jurisdictions', width: '33%' },
              { field: 'resident', label: 'Resident Entities', width: '33%' },
              { field: 'dormant', label: 'Dormant Entities', width: '34%' }
            ]
            const rows = data?.countryReporting?.residentEntitiesNames && Array.isArray(data.countryReporting.residentEntitiesNames) && data.countryReporting.residentEntitiesNames.length > 0
              ? data.countryReporting.residentEntitiesNames.map((item) => ({
                  tax: item.tax || '-',
                  resident: item.resident || '-',
                  dormant: item.dormant || '-'
                }))
              : []
            return <StyledReportTable columns={columns} rows={rows} showSerialNumber={true} />
          })()}
        </div>

        {/* Subsection b.ii: Primary activities */}
        <div style={{ marginTop: '24px' }}>
          <p className={reportStyles.consolidatingTitle} style={{ marginBottom: '12px', fontSize: '16px' }}>
            ii. Describe the primary activities of the organization within each tax jurisdiction.
          </p>
          {(() => {
            const columns = [
              { field: 'tax', label: 'Tax Jurisdictions', width: '50%' },
              { field: 'primaryAcitity', label: 'Primary Activities', width: '50%' }
            ]
            const rows = data?.countryReporting?.primaryActivitiesByTaxJurisdiction && Array.isArray(data.countryReporting.primaryActivitiesByTaxJurisdiction) && data.countryReporting.primaryActivitiesByTaxJurisdiction.length > 0
              ? data.countryReporting.primaryActivitiesByTaxJurisdiction.map((item) => ({
                  tax: item.tax || '-',
                  primaryAcitity: item.primaryAcitity || '-'
                }))
              : []
            return <StyledReportTable columns={columns} rows={rows} showSerialNumber={true} />
          })()}
        </div>

        {/* Subsection b.iii: Employee count */}
        <div style={{ marginTop: '24px' }}>
          <p className={reportStyles.consolidatingTitle} style={{ marginBottom: '12px', fontSize: '16px' }}>
            iii. Report the number of employees, indicating the basis of calculation (e.g., headcount or full-time equivalent) and ensuring consistency across all jurisdictions.
          </p>
          {(() => {
            const columns = [
              { field: 'tax', label: 'Tax Jurisdictions', width: '25%' },
              { field: 'numberOfEmployee', label: 'Number of Employees', width: '25%' },
              { field: 'basiCalculation', label: 'Basis of Calculation', width: '25%' },
              { field: 'consistency', label: 'Consistency across Jurisdictions', width: '25%' }
            ]
            const rows = data?.countryReporting?.employeeCountByJurisdiction && Array.isArray(data.countryReporting.employeeCountByJurisdiction) && data.countryReporting.employeeCountByJurisdiction.length > 0
              ? data.countryReporting.employeeCountByJurisdiction.map((item) => ({
                  tax: item.tax || '-',
                  numberOfEmployee: item.numberOfEmployee !== undefined && item.numberOfEmployee !== null && item.numberOfEmployee !== ''
                    ? (typeof item.numberOfEmployee === 'number' ? item.numberOfEmployee.toLocaleString() : item.numberOfEmployee)
                    : '-',
                  basiCalculation: item.basiCalculation || '-',
                  consistency: item.consistency || '-'
                }))
              : []
            return <StyledReportTable columns={columns} rows={rows} showSerialNumber={true} />
          })()}
        </div>

        {/* Subsection b.iv: Third-party revenue */}
        <div style={{ marginTop: '24px' }}>
          <p className={reportStyles.consolidatingTitle} style={{ marginBottom: '12px', fontSize: '16px' }}>
            iv. Disclose revenues from third-party sales for each tax jurisdiction.
          </p>
          {(() => {
            const columns = [
              { field: 'tax', label: 'Tax Jurisdictions', width: '50%' },
              { field: 'revenue', label: 'Revenues from Third-Party Sales (Currency)', width: '50%' }
            ]
            const rows = data?.countryReporting?.thirdPartyRevenueByJurisdiction && Array.isArray(data.countryReporting.thirdPartyRevenueByJurisdiction) && data.countryReporting.thirdPartyRevenueByJurisdiction.length > 0
              ? data.countryReporting.thirdPartyRevenueByJurisdiction.map((item) => ({
                  tax: item.tax || '-',
                  revenue: item.revenue || '-'
                }))
              : []
            return <StyledReportTable columns={columns} rows={rows} showSerialNumber={true} />
          })()}
        </div>

        {/* Subsection b.v: Intra-group revenue */}
        <div style={{ marginTop: '24px' }}>
          <p className={reportStyles.consolidatingTitle} style={{ marginBottom: '12px', fontSize: '16px' }}>
            v. Detail revenues from intra-group transactions with other tax jurisdictions, specifying transactions within and outside the jurisdiction.
          </p>
          {(() => {
            const columns = [
              { field: 'tax', label: 'Tax Jurisdictions', width: '33%' },
              { field: 'jurisdiction', label: 'Revenues from Intra-Group Transactions within the Jurisdiction', width: '33%' },
              { field: 'jurisdictionOutside', label: 'Revenues from Intra-Group Transactions outside the Jurisdiction', width: '34%' }
            ]
            const rows = data?.countryReporting?.intraGroupRevenueByJurisdiction && Array.isArray(data.countryReporting.intraGroupRevenueByJurisdiction) && data.countryReporting.intraGroupRevenueByJurisdiction.length > 0
              ? data.countryReporting.intraGroupRevenueByJurisdiction.map((item) => ({
                  tax: item.tax || '-',
                  jurisdiction: item.jurisdiction || '-',
                  jurisdictionOutside: item.jurisdictionOutside || '-'
                }))
              : []
            return <StyledReportTable columns={columns} rows={rows} showSerialNumber={true} />
          })()}
        </div>

        {/* Subsection b.vi: Pre-tax profit/loss */}
        <div style={{ marginTop: '24px' }}>
          <p className={reportStyles.consolidatingTitle} style={{ marginBottom: '12px', fontSize: '16px' }}>
            vi. State the profit/loss before tax for each tax jurisdiction.
          </p>
          {(() => {
            const columns = [
              { field: 'tax', label: 'Tax Jurisdictions', width: '50%' },
              { field: 'beforeTax', label: 'Profit/Loss Before Tax', width: '50%' }
            ]
            const rows = data?.countryReporting?.preTaxProfitLossByJurisdiction && Array.isArray(data.countryReporting.preTaxProfitLossByJurisdiction) && data.countryReporting.preTaxProfitLossByJurisdiction.length > 0
              ? data.countryReporting.preTaxProfitLossByJurisdiction.map((item) => ({
                  tax: item.tax || '-',
                  beforeTax: item.beforeTax || '-'
                }))
              : []
            return <StyledReportTable columns={columns} rows={rows} showSerialNumber={true} />
          })()}
        </div>

        {/* Subsection b.vii: Tangible assets */}
        <div style={{ marginTop: '24px' }}>
          <p className={reportStyles.consolidatingTitle} style={{ marginBottom: '12px', fontSize: '16px' }}>
            vii. Specify the total tangible assets other than cash and cash equivalents held within each tax jurisdiction.
          </p>
          {(() => {
            const columns = [
              { field: 'tax', label: 'Tax Jurisdictions', width: '50%' },
              { field: 'total', label: 'Total Tangible Assets (excluding cash and cash equivalents)', width: '50%' }
            ]
            const rows = data?.countryReporting?.tangibleAssetsByTaxJurisdiction && Array.isArray(data.countryReporting.tangibleAssetsByTaxJurisdiction) && data.countryReporting.tangibleAssetsByTaxJurisdiction.length > 0
              ? data.countryReporting.tangibleAssetsByTaxJurisdiction.map((item) => ({
                  tax: item.tax || '-',
                  total: item.total || '-'
                }))
              : []
            return <StyledReportTable columns={columns} rows={rows} showSerialNumber={true} />
          })()}
        </div>

        {/* Subsection b.viii: Cash basis corporate income tax paid */}
        <div style={{ marginTop: '24px' }}>
          <p className={reportStyles.consolidatingTitle} style={{ marginBottom: '12px', fontSize: '16px' }}>
            viii. Report corporate income tax paid on a cash basis, including taxes withheld and paid on behalf of employees.
          </p>
          {(() => {
            const columns = [
              { field: 'tax', label: 'Tax Jurisdictions', width: '50%' },
              { field: 'taxPaid', label: 'Corporate Income Tax Paid (Cash Basis)', width: '50%' }
            ]
            const rows = data?.countryReporting?.cashBasisCorporateIncomeTaxPaid && Array.isArray(data.countryReporting.cashBasisCorporateIncomeTaxPaid) && data.countryReporting.cashBasisCorporateIncomeTaxPaid.length > 0
              ? data.countryReporting.cashBasisCorporateIncomeTaxPaid.map((item) => ({
                  tax: item.tax || '-',
                  taxPaid: item.taxPaid || '-'
                }))
              : []
            return <StyledReportTable columns={columns} rows={rows} showSerialNumber={true} />
          })()}
        </div>

        {/* Subsection b.ix: Accrued corporate income tax */}
        <div style={{ marginTop: '24px' }}>
          <p className={reportStyles.consolidatingTitle} style={{ marginBottom: '12px', fontSize: '16px' }}>
            ix. Provide the corporate income tax accrued on profit/loss for each tax jurisdiction.
          </p>
          {(() => {
            const columns = [
              { field: 'tax', label: 'Tax Jurisdictions', width: '50%' },
              { field: 'incomeTax', label: 'Corporate Income Tax Accrued on Profit / Loss', width: '50%' }
            ]
            const rows = data?.countryReporting?.accruedCorporateIncomeTax && Array.isArray(data.countryReporting.accruedCorporateIncomeTax) && data.countryReporting.accruedCorporateIncomeTax.length > 0
              ? data.countryReporting.accruedCorporateIncomeTax.map((item) => ({
                  tax: item.tax || '-',
                  incomeTax: item.incomeTax || '-'
                }))
              : []
            return <StyledReportTable columns={columns} rows={rows} showSerialNumber={true} />
          })()}
        </div>

        {/* Subsection b.x: Tax rate variance explanation */}
        <div style={{ marginTop: '24px' }}>
          <p className={reportStyles.consolidatingTitle} style={{ marginBottom: '12px', fontSize: '16px' }}>
            x. Explain the reasons for any difference between the accrued corporate income tax on profit/loss and the tax due if the statutory tax rate is applied.
          </p>
          {(() => {
            const columns = [
              { field: 'tax', label: 'Tax Jurisdictions', width: '50%' },
              { field: 'reason', label: 'Reason for Difference', width: '50%' }
            ]
            const rows = data?.countryReporting?.taxRateVarianceExplanation && Array.isArray(data.countryReporting.taxRateVarianceExplanation) && data.countryReporting.taxRateVarianceExplanation.length > 0
              ? data.countryReporting.taxRateVarianceExplanation.map((item) => ({
                  tax: item.tax || '-',
                  reason: item.reason || '-'
                }))
              : []
            return <StyledReportTable columns={columns} rows={rows} showSerialNumber={true} />
          })()}
        </div>

        {/* Subsection c: Disclosure reporting period */}
        <div style={{ marginTop: '24px' }}>
          <p className={reportStyles.consolidatingTitle} style={{ marginBottom: '12px', fontSize: '16px' }}>
            c. Indicate the time period covered by the information reported in Disclosure 207-4, ensuring alignment with financial reporting and explaining any deviations from the reporting period.
          </p>
          {(() => {
            const columns = [
              { field: 'tax', label: 'Tax Jurisdictions', width: '33%' },
              { field: 'financialReporting', label: 'Alignment with Financial Reporting', width: '33%' },
              { field: 'deviation', label: 'Explaination of deviation', width: '34%' }
            ]
            const rows = data?.countryReporting?.disclosureReportingPeriod && Array.isArray(data.countryReporting.disclosureReportingPeriod) && data.countryReporting.disclosureReportingPeriod.length > 0
              ? data.countryReporting.disclosureReportingPeriod.map((item) => ({
                  tax: item.tax || '-',
                  financialReporting: item.financialReporting || '-',
                  deviation: item.deviation || '-'
                }))
              : []
            return <StyledReportTable columns={columns} rows={rows} showSerialNumber={true} />
          })()}
        </div>
      </div>
    </div>
  )
}

export default GRI200s